import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { Building2, ChevronLeft, History, RefreshCw } from "lucide-react";
import FieldConfigurationAPI from "../../../../../lib/api/FieldConfiguration";
import { FieldConfigurationContext } from "../../../../../lib/context/FieldConfigurationContext";
import SearchBar from "../../components/SearchBar";
import SortableTable from "../../components/SortableTable";
import PaginationControls from "../../components/PaginationControls";
import Button from "../../../../common/Button";
import Validation from "../../../../common/Validation";
import AssignTeamModal from "./AssignTeamModal";
import JobApprovalTeamHistoryModal from "./JobApprovalTeamHistoryModal";
import { getActiveCompanyOptions } from "../AccessGroupsList";
import { SELECTED_COMPANY_KEY } from "../../../../../lib/local/SelectedCompany";
import "../../components/DetailHeader.css";
import "../../style.css";
import "../RolesList.css";
import "./JobAssignments.css";

export const SELECTED_COMPANY_STORAGE_KEY = SELECTED_COMPANY_KEY;

export default function JobAssignments({ onBack }) {
  const { approvalTeams, fetchApprovalTeams, companies } = useContext(
    FieldConfigurationContext
  );

  const [jobs, setJobs] = useState({ data: [], loading: false, error: null });
  const [selectedCompanyUuid, setSelectedCompanyUuid] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [paginationConfig, setPaginationConfig] = useState({
    currentPage: 1,
    pageSize: 20,
  });
  const [selectedJobUuids, setSelectedJobUuids] = useState([]);
  // Job uuid -> approval team uuid (or null). Only rows the admin has
  // touched appear here — everything else still shows the saved value.
  const [pendingAssignments, setPendingAssignments] = useState({});
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [historyJob, setHistoryJob] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    fetchApprovalTeams();
  }, [fetchApprovalTeams]);

  const companyOptions = useMemo(
    () =>
      getActiveCompanyOptions(companies, companies.configureApprovalGroupsData),
    [
      companies.data,
      companies.codesMap,
      companies.enabledData,
      companies.configureApprovalGroupsData,
    ]
  );

  useEffect(() => {
    if (companyOptions.length === 0) return;
    if (
      selectedCompanyUuid &&
      companyOptions.some((opt) => opt.value === selectedCompanyUuid)
    ) {
      return;
    }
    const stored = localStorage.getItem(SELECTED_COMPANY_STORAGE_KEY);
    const initial =
      stored && companyOptions.some((opt) => opt.value === stored)
        ? stored
        : companyOptions[0].value;
    setSelectedCompanyUuid(initial);
  }, [companyOptions, selectedCompanyUuid]);

  const fetchJobs = useCallback(async () => {
    if (!selectedCompanyUuid) return;
    setJobs((prev) => ({ ...prev, loading: true, error: null }));
    const response = await FieldConfigurationAPI.getJobs(
      null,
      selectedCompanyUuid
    );
    if (response.status === 200 && response.data) {
      setJobs({ data: response.data, loading: false, error: null });
    } else {
      setJobs({ data: [], loading: false, error: "Failed to load jobs." });
    }
  }, [selectedCompanyUuid]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleCompanyChange = (e) => {
    const uuid = e.target.value;
    setSelectedCompanyUuid(uuid);
    localStorage.setItem(SELECTED_COMPANY_STORAGE_KEY, uuid);
    setSelectedJobUuids([]);
    setPendingAssignments({});
  };

  const teamOptions = useMemo(
    () =>
      (approvalTeams.data || []).filter(
        (t) => t.company_uuid === selectedCompanyUuid
      ),
    [approvalTeams.data, selectedCompanyUuid]
  );

  const filteredJobs = useMemo(() => {
    const byStatus = statusFilter
      ? jobs.data.filter((job) => (job.status || "active") === statusFilter)
      : jobs.data;
    if (!searchTerm) return byStatus;
    const lower = searchTerm.toLowerCase();
    return byStatus.filter(
      (job) =>
        job.code?.toLowerCase().includes(lower) ||
        job.description?.toLowerCase().includes(lower) ||
        job.customer_code?.toLowerCase().includes(lower) ||
        job.customer_name?.toLowerCase().includes(lower)
    );
  }, [jobs.data, searchTerm, statusFilter]);

  const sortedJobs = useMemo(() => {
    if (!sortConfig.key) return filteredJobs;
    return [...filteredJobs].sort((a, b) => {
      const aValue = a[sortConfig.key] || "";
      const bValue = b[sortConfig.key] || "";
      return sortConfig.direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [filteredJobs, sortConfig]);

  const paginatedJobs = useMemo(() => {
    const startIndex =
      (paginationConfig.currentPage - 1) * paginationConfig.pageSize;
    return sortedJobs.slice(startIndex, startIndex + paginationConfig.pageSize);
  }, [sortedJobs, paginationConfig]);

  useEffect(() => {
    setPaginationConfig((prev) => ({ ...prev, currentPage: 1 }));
  }, [filteredJobs.length]);

  const handleSort = (columnKey) => {
    setSortConfig((prev) => ({
      key: columnKey,
      direction:
        prev.key === columnKey && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getRowTeamUuid = (job) =>
    (job.uuid in pendingAssignments
      ? pendingAssignments[job.uuid]
      : job.approval_team_uuid) || "";

  const handleRowTeamChange = (job, teamUuid) => {
    setPendingAssignments((prev) => ({
      ...prev,
      [job.uuid]: teamUuid || null,
    }));
  };

  const allCurrentPageSelected =
    paginatedJobs.length > 0 &&
    paginatedJobs.every((job) => selectedJobUuids.includes(job.uuid));

  const handleSelectAllOnPage = (checked) => {
    setSelectedJobUuids((prev) => {
      const pageUuids = paginatedJobs.map((job) => job.uuid);
      if (checked) {
        return [...new Set([...prev, ...pageUuids])];
      }
      return prev.filter((uuid) => !pageUuids.includes(uuid));
    });
  };

  const handleSelectAllFiltered = () => {
    setSelectedJobUuids(sortedJobs.map((job) => job.uuid));
  };

  const handleSelectNone = () => setSelectedJobUuids([]);

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setSelectedJobUuids([]);
  };

  const handleRowSelect = (job, checked) => {
    setSelectedJobUuids((prev) =>
      checked ? [...prev, job.uuid] : prev.filter((uuid) => uuid !== job.uuid)
    );
  };

  const handleApplyToSelection = (teamUuid) => {
    setPendingAssignments((prev) => {
      const next = { ...prev };
      selectedJobUuids.forEach((uuid) => {
        next[uuid] = teamUuid || null;
      });
      return next;
    });
    setShowAssignModal(false);
  };

  const handleShowHistory = (job, event) => {
    event.stopPropagation();
    setHistoryJob(job);
  };

  const actions = [
    {
      label: "History",
      icon: History,
      onClick: handleShowHistory,
    },
  ];

  const pendingCount = Object.keys(pendingAssignments).length;

  const handleCancelChanges = () => {
    setPendingAssignments({});
    setError("");
    setSuccess("");
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    setError("");
    setSuccess("");

    const updates = Object.entries(pendingAssignments).map(
      ([uuid, approvalTeamUuid]) => ({
        uuid,
        approvalTeamUuid,
        companyUuid: selectedCompanyUuid,
      })
    );

    const result = await FieldConfigurationAPI.assignJobApprovalTeams(updates);
    setIsSaving(false);

    if (result.status === 200 || result.status === 207) {
      const { successful, failed } = result.data || {};
      if (failed > 0) {
        setError(`${failed} job(s) failed to update; ${successful} succeeded.`);
      } else {
        setSuccess(`Successfully updated ${successful} job assignment(s).`);
        setTimeout(() => setSuccess(""), 5000);
      }
      setPendingAssignments({});
      setSelectedJobUuids([]);
      fetchJobs();
    } else {
      setError("Failed to save job assignments. Please try again.");
    }
  };

  const columns = [
    {
      key: "select",
      label: (
        <input
          type="checkbox"
          checked={allCurrentPageSelected}
          onChange={(e) => handleSelectAllOnPage(e.target.checked)}
          style={{ width: "16px", height: "16px", cursor: "pointer" }}
          aria-label="Select all jobs on current page"
        />
      ),
      width: "44px",
      align: "center",
      sortable: false,
      render: (job) => (
        <input
          type="checkbox"
          checked={selectedJobUuids.includes(job.uuid)}
          onChange={(e) => handleRowSelect(job, e.target.checked)}
          style={{ width: "16px", height: "16px", cursor: "pointer" }}
        />
      ),
    },
    {
      key: "code",
      label: "Job Number",
      sortable: true,
      width: "110px",
    },
    {
      key: "description",
      label: "Job Name",
      sortable: true,
      width: "220px",
      render: (job, value) => <span title={value || ""}>{value || "—"}</span>,
    },
    {
      key: "job_class",
      label: "Class",
      sortable: true,
      width: "90px",
      render: (job, value) => value || "—",
    },
    {
      key: "customer_code",
      label: "Customer Number",
      sortable: true,
      width: "150px",
      render: (job, value) => value || "—",
    },
    {
      key: "customer_name",
      label: "Customer Name",
      sortable: true,
      width: "200px",
      render: (job, value) => <span title={value || ""}>{value || "—"}</span>,
    },
    {
      key: "approval_team",
      label: "Approval Team",
      sortable: false,
      width: "200px",
      render: (job) => (
        <select
          className="job-assignments__team-select"
          value={getRowTeamUuid(job)}
          onChange={(e) => handleRowTeamChange(job, e.target.value)}
          onClick={(e) => e.stopPropagation()}
        >
          <option value="">— None —</option>
          {teamOptions.map((team) => (
            <option key={team.uuid} value={team.uuid}>
              {team.name}
            </option>
          ))}
        </select>
      ),
    },
  ];

  return (
    <div className="roles-list">
      <div className="detail-header-breadcrumb-row">
        <button
          type="button"
          className="detail-header-back-link"
          onClick={onBack}
        >
          <ChevronLeft size={16} />
          Back to Approval Teams
        </button>
      </div>

      <div className="roles-list__header">
        <div className="roles-list__header-top">
          <div className="roles-list__header-left">
            <h2 className="roles-list__title">Job Assignments</h2>
            {companyOptions.length > 0 && (
              <div className="roles-list__company-filter">
                <Building2
                  size={18}
                  style={{ color: "rgba(100, 116, 139, 1)", flexShrink: 0 }}
                />
                <select
                  className="roles-list__company-select"
                  value={selectedCompanyUuid}
                  onChange={handleCompanyChange}
                  title={
                    companyOptions.find(
                      (opt) => opt.value === selectedCompanyUuid
                    )?.label || ""
                  }
                >
                  {companyOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search jobs..."
          />
          <div className="roles-list__actions">
            <Button
              className="button--secondary"
              onClick={() => fetchJobs()}
              disabled={jobs.loading}
              title="Refresh jobs"
            >
              <RefreshCw size={18} style={{ marginRight: "6px" }} />
              Refresh
            </Button>
            <Button
              className="button--secondary"
              onClick={handleCancelChanges}
              disabled={pendingCount === 0 || isSaving}
            >
              Cancel
            </Button>
            <Button
              className="button--primary"
              onClick={handleSaveChanges}
              disabled={pendingCount === 0 || isSaving}
            >
              {isSaving
                ? "Saving..."
                : `Save Changes${pendingCount ? ` (${pendingCount})` : ""}`}
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <div style={{ marginTop: "16px", marginBottom: "4px" }}>
          <Validation className="danger">{error}</Validation>
        </div>
      )}
      {success && (
        <div style={{ marginTop: "16px", marginBottom: "4px" }}>
          <Validation className="success">{success}</Validation>
        </div>
      )}

      <div className="job-assignments__toolbar">
        <Button className="button--secondary" onClick={handleSelectAllFiltered}>
          Select All
        </Button>
        <Button className="button--secondary" onClick={handleSelectNone}>
          Select None
        </Button>
        <Button
          className="button--primary"
          onClick={() => setShowAssignModal(true)}
          disabled={selectedJobUuids.length === 0}
        >
          Assign Team to Selection
        </Button>
        <div className="job-assignments__toolbar-spacer" />
        {selectedJobUuids.length > 0 && (
          <span className="job-assignments__summary">
            {selectedJobUuids.length} selected
          </span>
        )}
        <label className="job-assignments__status-label">
          Job Status
          <select
            className="job-assignments__team-select job-assignments__status-filter"
            value={statusFilter}
            onChange={handleStatusFilterChange}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="closed">Closed</option>
            <option value="">All Statuses</option>
          </select>
        </label>
      </div>

      {jobs.loading && (
        <div className="roles-list__loading">
          <p>Loading jobs...</p>
        </div>
      )}

      {jobs.error && (
        <div className="roles-list__error">
          <p>{jobs.error}</p>
          <Button onClick={() => fetchJobs()}>Retry</Button>
        </div>
      )}

      {!jobs.loading && !jobs.error && (
        <>
          <SortableTable
            data={paginatedJobs}
            columns={columns}
            sortConfig={sortConfig}
            onSort={handleSort}
            actions={actions}
            emptyMessage="No jobs found for this company."
          />
          <PaginationControls
            currentPage={paginationConfig.currentPage}
            pageSize={paginationConfig.pageSize}
            totalItems={sortedJobs.length}
            onPageChange={(newPage) => {
              setPaginationConfig((prev) => ({
                ...prev,
                currentPage: newPage,
              }));
            }}
          />
        </>
      )}

      <AssignTeamModal
        open={showAssignModal}
        teamOptions={teamOptions}
        selectedCount={selectedJobUuids.length}
        onApply={handleApplyToSelection}
        onClose={() => setShowAssignModal(false)}
      />

      <JobApprovalTeamHistoryModal
        job={historyJob}
        approvalTeams={approvalTeams.data}
        onClose={() => setHistoryJob(null)}
      />
    </div>
  );
}
